/**
 * Profile Page - User profile with tabs
 * Based on mobile app ProfileScreenOptimized.tsx
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Settings, MessageSquare, Grid3x3, Heart, Bookmark, Lock, Share2 } from 'lucide-react'
import ProfileHeader from '../components/profile/ProfileHeader'
import ProfileTabs, { TabType } from '../components/profile/ProfileTabs'
import MediaGrid from '../components/profile/MediaGrid'
import EmptyState from '../components/profile/EmptyState'
import Post from '../components/Post'
import api from '../services/api'

export default function Profile() {
  const navigate = useNavigate()
  const { userId } = useParams() // Optional userId from URL params
  const [activeTab, setActiveTab] = useState<TabType>('posts')
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState<any>(null)
  const [currentUserId, setCurrentUserId] = useState<string | null>(null)

  // Tab data states
  const [postsData, setPostsData] = useState<any[]>([])
  const [mediaData, setMediaData] = useState<any[]>([])
  const [repliesData, setRepliesData] = useState<any[]>([])
  const [likesData, setLikesData] = useState<any[]>([])
  const [savedData, setSavedData] = useState<any[]>([])
  const [privateData, setPrivateData] = useState<any[]>([])
  const [sharedData, setSharedData] = useState<any[]>([])

  const [isFollowing, setIsFollowing] = useState(false)
  const isOwnProfile = !userId || (currentUserId && userId === currentUserId)

  useEffect(() => {
    loadProfile()
  }, [userId])

  useEffect(() => {
    if (user) {
      loadTabData()
    }
  }, [activeTab, user])

  const loadProfile = async () => {
    try {
      setLoading(true)

      // Get current user profile or specific user profile
      const profile = await api.getUserProfile(userId)

      if (profile) {
        setUser({
          name: profile.name || 'User',
          username: profile.username || 'user',
          avatar: profile.avatar,
          bio: profile.bio || '',
          verified: profile.verified || false,
          stats: {
            followers: profile.stats?.followers || 0,
            following: profile.stats?.following || 0,
            posts: profile.stats?.posts || 0,
          },
        })
        setCurrentUserId(profile.id || profile.userId)
        if (profile.is_following !== undefined) {
          setIsFollowing(profile.is_following)
        }
      }
    } catch (error) {
      console.error('Error loading profile:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadTabData = async () => {
    if (!user) return

    const targetUserId = userId || currentUserId
    if (!targetUserId) return

    try {
      switch (activeTab) {
        case 'posts':
          const postsResult = await api.getUserPosts(targetUserId, { limit: 20 })
          if (postsResult.success) {
            setPostsData(postsResult.posts)
          }
          break

        case 'media':
          // Filter posts with media from user's posts
          const mediaResult = await api.getUserPosts(targetUserId, { limit: 50 })
          if (mediaResult.success) {
            const mediaItems = mediaResult.posts
              .filter(post => post.mediaUri)
              .map(post => ({
                id: post.postId,
                url: post.mediaUri,
                type: post.mediaType?.includes('video') ? 'video' : 'photo',
                thumbnail: post.thumbnailUri || post.mediaUri,
              }))
            setMediaData(mediaItems)
          }
          break

        case 'likes':
          // This would need a backend endpoint for liked posts
          // For now, use localStorage
          const likedPostIds = JSON.parse(localStorage.getItem('liked_posts') || '[]')
          setLikesData([]) // TODO: Fetch actual liked posts from backend
          break

        case 'saved':
          // Use localStorage for saved posts
          const savedPostIds = JSON.parse(localStorage.getItem('bookmarked_posts') || '[]')
          setSavedData([]) // TODO: Fetch actual saved posts from backend
          break

        default:
          break
      }
    } catch (error) {
      console.error('Error loading tab data:', error)
    }
  }

  const handleFollowToggle = useCallback(async () => {
    if (!userId) return
    const prev = isFollowing
    setIsFollowing(!prev)

    try {
      if (prev) {
        await api.unfollowUser(userId)
      } else {
        await api.followUser(userId)
      }
      // Update follower count
      setUser((u: any) => u ? {
        ...u,
        stats: {
          ...u.stats,
          followers: prev ? Math.max(0, u.stats.followers - 1) : u.stats.followers + 1,
        },
      } : u)
    } catch {
      setIsFollowing(prev)
    }
  }, [userId, isFollowing])

  const handleAvatarClick = () => {
    if (isOwnProfile) {
      navigate('/edit-profile')
    }
  }

  const handleSettingsClick = () => {
    navigate('/settings')
  }

  const handleCreatePost = () => {
    navigate('/create')
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'posts':
        if (postsData.length === 0) {
          return (
            <EmptyState
              icon={MessageSquare}
              title="No posts yet"
              description={isOwnProfile ? "You haven't posted anything yet. Share your first post!" : "This user hasn't posted anything yet."}
              action={isOwnProfile ? { label: 'Create Post', onClick: handleCreatePost } : undefined}
            />
          )
        }
        return (
          <div>
            {postsData.map((post) => (
              <Post key={post.id} post={post} />
            ))}
          </div>
        )

      case 'media':
        if (mediaData.length === 0) {
          return (
            <EmptyState
              icon={Grid3x3}
              title="No media yet"
              description={isOwnProfile ? "Posts with photos and videos you share will appear here." : "This user hasn't shared any media yet."}
            />
          )
        }
        return <MediaGrid media={mediaData} />

      case 'replies':
        if (repliesData.length === 0) {
          return (
            <EmptyState
              icon={MessageSquare}
              title="No replies yet"
              description={isOwnProfile ? "Your replies to other posts will appear here." : "This user hasn't replied to any posts yet."}
            />
          )
        }
        return (
          <div>
            {repliesData.map((post) => (
              <Post key={post.id} post={post} />
            ))}
          </div>
        )

      case 'likes':
        if (likesData.length === 0) {
          return (
            <EmptyState
              icon={Heart}
              title="No likes yet"
              description={isOwnProfile ? "Posts you like will appear here." : "This user hasn't liked any posts yet."}
            />
          )
        }
        return (
          <div>
            {likesData.map((post) => (
              <Post key={post.id} post={post} />
            ))}
          </div>
        )

      case 'saved':
        if (savedData.length === 0) {
          return (
            <EmptyState
              icon={Bookmark}
              title="No saved posts"
              description="Save posts to read them later. They'll appear here."
            />
          )
        }
        return (
          <div>
            {savedData.map((post) => (
              <Post key={post.id} post={post} />
            ))}
          </div>
        )

      case 'private':
        if (privateData.length === 0) {
          return (
            <EmptyState
              icon={Lock}
              title="No private posts"
              description="Your private drafts and unlisted posts will appear here."
            />
          )
        }
        return (
          <div>
            {privateData.map((post) => (
              <Post key={post.id} post={post} />
            ))}
          </div>
        )

      case 'shared':
        if (sharedData.length === 0) {
          return (
            <EmptyState
              icon={Share2}
              title="Nothing shared yet"
              description="Posts shared privately with you will appear here."
            />
          )
        }
        return (
          <div>
            {sharedData.map((post) => (
              <Post key={post.id} post={post} />
            ))}
          </div>
        )

      default:
        return null
    }
  }

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-white flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-onrecord-orange"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 bg-white bg-opacity-90 backdrop-blur-sm border-b border-gray-200 z-20">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-700 hover:text-onrecord-orange rounded-full p-1 hover:bg-orange-50"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold">{user.name}</h1>
              <p className="text-xs text-text-secondary">{user.stats.posts} posts</p>
            </div>
          </div>

          {/* Settings Icon (only show on own profile) */}
          {isOwnProfile && (
            <button
              onClick={handleSettingsClick}
              className="text-gray-700 hover:text-onrecord-orange rounded-full p-2 hover:bg-orange-50"
            >
              <Settings className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Profile Header */}
      <ProfileHeader user={user} onAvatarClick={handleAvatarClick} />

      {/* Edit Profile / Follow Button */}
      <div className="px-4 py-3 border-b border-gray-200">
        {isOwnProfile ? (
          <button
            onClick={() => navigate('/edit-profile')}
            className="w-full btn btn-outline"
          >
            Edit Profile
          </button>
        ) : (
          <button
            onClick={handleFollowToggle}
            className={`w-full py-2 px-6 rounded-full text-sm font-semibold transition-colors ${
              isFollowing
                ? 'bg-white text-gray-900 border border-gray-300 hover:border-red-300 hover:text-red-600'
                : 'bg-gray-900 text-white hover:bg-gray-800'
            }`}
          >
            {isFollowing ? 'Following' : 'Follow'}
          </button>
        )}
      </div>

      {/* Tabs */}
      <ProfileTabs
        activeTab={activeTab}
        onTabChange={setActiveTab}
        isOwnProfile={isOwnProfile}
      />

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {renderTabContent()}
      </div>
    </div>
  )
}
